import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ClientApiError } from "../auth/api";
import { Badge, ErrorState, Skeleton } from "../components/ui";
import { getPublicPedigree, type PedigreeDocument } from "../pedigree/api";
import { PedigreeExplorer, type GenerationCount } from "../pedigree/PedigreeTree";

function errorFrom(caught: unknown): { message: string; requestId?: string } {
  if (caught instanceof ClientApiError) {
    return {
      message: caught.problem.message,
      requestId: caught.problem.requestId,
    };
  }
  return { message: "Không thể xử lý yêu cầu. Vui lòng thử lại." };
}

function generationFrom(value: string | null): GenerationCount {
  return value === "4" ? 4 : value === "5" ? 5 : 3;
}

function sexLabel(value: "male" | "female" | "unknown"): string {
  return value === "male" ? "Đực" : value === "female" ? "Cái" : "Chưa rõ";
}

function sexSymbol(value: "male" | "female" | "unknown"): string {
  return value === "male" ? "♂" : value === "female" ? "♀" : "•";
}

function PedigreeDogSummary({ pedigree }: { pedigree: PedigreeDocument }) {
  const dog = pedigree.nodes.find((node) => node.generation === 0)?.dog;
  if (!dog) return null;

  const initials = dog.registeredName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  const facts = [
    { label: "Giới tính", value: sexLabel(dog.sex), symbol: sexSymbol(dog.sex) },
    { label: "Năm sinh", value: dog.birthYear ? String(dog.birthYear) : "Chưa cập nhật" },
    { label: "Giống", value: dog.breedName ?? "Chưa cập nhật" },
    { label: "Màu lông", value: dog.colorName ?? "Chưa cập nhật" },
  ];

  return (
    <section className="pedigree-dog-summary" aria-labelledby="pedigree-dog-summary-title">
      <div className="pedigree-dog-summary__media">
        {dog.thumbnailUrl ? (
          <img
            src={dog.thumbnailUrl}
            alt={`Ảnh hồ sơ ${dog.registeredName}`}
            width="320"
            height="320"
          />
        ) : (
          <div
            className="pedigree-dog-summary__placeholder"
            role="img"
            aria-label="Hồ sơ chưa có ảnh đã duyệt"
          >
            <strong>{initials || sexSymbol(dog.sex)}</strong>
            <span>Chưa có ảnh đã duyệt</span>
          </div>
        )}
        <span className="pedigree-dog-summary__photo-status">
          {dog.thumbnailUrl ? "Ảnh hồ sơ" : "Đang chờ ảnh"}
        </span>
      </div>

      <div className="pedigree-dog-summary__content">
        <header className="pedigree-dog-summary__heading">
          <div>
            <p className="eyebrow">Dog information</p>
            <h2 id="pedigree-dog-summary-title">{dog.registeredName}</h2>
            <p className="pedigree-dog-summary__meta">
              Hồ sơ WorkingDog VN · phiên bản {dog.version}
            </p>
          </div>
          <Badge tone={dog.verified ? "success" : "warning"}>
            {dog.verified ? "Đã xác minh" : "Chờ xác minh"}
          </Badge>
        </header>

        <dl className="pedigree-dog-summary__facts">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>
                {fact.symbol ? <b aria-hidden="true">{fact.symbol}</b> : null}
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>

        <div className="pedigree-dog-summary__titles">
          <span>Danh hiệu</span>
          {dog.titles.length > 0 ? (
            <div>
              {dog.titles.slice(0, 6).map((title) => (
                <abbr key={title.code} title={title.name}>
                  {title.code}
                </abbr>
              ))}
            </div>
          ) : (
            <p>Chưa có danh hiệu đã xác minh.</p>
          )}
        </div>

        <p className="pedigree-dog-summary__approval-note">
          Chỉ thông tin và ảnh đã được admin hoặc moderator duyệt mới xuất hiện trong hồ sơ công khai.
        </p>

        <nav className="pedigree-dog-summary__tabs" aria-label="Điều hướng hồ sơ chó">
          <Link to={`/dogs/${dog.slug}`}>Thông tin chó</Link>
          <span aria-current="page">Phả hệ</span>
        </nav>
      </div>
    </section>
  );
}

export function PublicPedigreeProfilePage() {
  const { slug = "" } = useParams();
  const { principal } = useAuth();
  const [search, setSearch] = useSearchParams();
  const generations = generationFrom(search.get("generations"));
  const [pedigree, setPedigree] = useState<PedigreeDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPublicPedigree(slug, generations)
      .then((result) => {
        if (!active) return;
        setPedigree(result.pedigree);
        setError(null);
      })
      .catch((caught) => {
        if (active) setError(errorFrom(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [generations, slug]);

  const root = pedigree?.nodes.find((node) => node.generation === 0)?.dog;
  return (
    <main className="page-container pedigree-page" data-testid="public-pedigree-page">
      <header className="page-heading pedigree-heading">
        <div>
          <p className="eyebrow">WorkingDog VN · Pedigree</p>
          <h1>{root ? `Phả hệ ${root.registeredName}` : "Phả hệ chó làm việc"}</h1>
          <p>
            Cây phả hệ có nguồn, ảnh, trạng thái xác minh và lịch sử duyệt; dữ liệu chưa duyệt không được công khai.
          </p>
        </div>
        {principal && pedigree ? (
          <Link
            className="ui-button ui-button--secondary ui-button--md"
            to={`/member/dogs/${pedigree.rootDogId}/pedigree`}
          >
            Quản lý phả hệ
          </Link>
        ) : null}
      </header>

      {loading ? <Skeleton lines={10} /> : null}
      {!loading && error ? (
        <ErrorState
          title="Không tải được phả hệ"
          description={error.message}
          requestId={error.requestId}
        />
      ) : null}
      {!loading && !error && pedigree ? (
        <>
          <PedigreeDogSummary pedigree={pedigree} />
          <PedigreeExplorer
            pedigree={pedigree}
            onGenerationChange={(value) => setSearch({ generations: String(value) })}
          />
        </>
      ) : null}
    </main>
  );
}
